import api from '@/api';

export type UserSkillEntry = {
  tag: string;
  description?: string;
};

export type TaskSkillEntry = {
  tag: string;
  notes?: string;
};

export type TeamMemberSkillsProfile = {
  uid: string;
  email?: string;
  skills: UserSkillEntry[];
  skillsSummary?: string;
  openCount: number;
  overdueCount: number;
};

export type TaskAssignmentProposal = {
  taskId: string;
  assigneeUid: string | null;
  reason: string;
  releaseToClaim?: boolean;
};

export async function fetchMySkills(): Promise<{
  skills: UserSkillEntry[];
  skillsSummary?: string;
}> {
  const response = await api.get<{
    status: string;
    skills?: UserSkillEntry[];
    skillsSummary?: string;
  }>('/users/me/skills');
  if (response.data?.status !== 'success') {
    throw new Error('Unable to load skills');
  }
  return {
    skills: response.data.skills ?? [],
    skillsSummary: response.data.skillsSummary,
  };
}

export async function saveMySkills(payload: {
  skills: UserSkillEntry[];
}): Promise<void> {
  const response = await api.put<{ status: string; message?: string }>(
    '/users/me/skills',
    payload
  );
  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Unable to save skills');
  }
}

export async function suggestSkillsFromAnswers(answers: {
  workAreas: string;
  toolsAndTech: string;
  avoidTasks?: string;
}): Promise<UserSkillEntry[]> {
  const response = await api.post<{ status: string; skills?: UserSkillEntry[]; message?: string }>(
    '/ai/skills/suggest-from-answers',
    answers
  );
  if (response.data?.status !== 'success' || !response.data.skills?.length) {
    throw new Error(response.data?.message ?? 'Could not suggest skills');
  }
  return response.data.skills;
}

export async function fetchTeamSkillsRoster(pid?: string): Promise<TeamMemberSkillsProfile[]> {
  const response = await api.get<{ status: string; members?: TeamMemberSkillsProfile[] }>(
    '/sessions/tasks/team-skills',
    { params: pid ? { pid } : undefined }
  );
  return response.data?.status === 'success' ? response.data.members ?? [] : [];
}

export async function fetchClaimableTasks(pid?: string): Promise<
  Array<{ id: string; title: string; sid: string; skills: TaskSkillEntry[] }>
> {
  const response = await api.get<{
    status: string;
    tasks?: Array<{ id: string; title: string; sid: string; skills: TaskSkillEntry[] }>;
  }>('/sessions/tasks/claimable', { params: pid ? { pid } : undefined });
  return response.data?.status === 'success' ? response.data.tasks ?? [] : [];
}

export async function claimTask(taskId: string, pid: string): Promise<void> {
  const response = await api.post<{ status: string; message?: string }>(
    `/sessions/task/${taskId}/claim`,
    { pid }
  );
  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Unable to claim task');
  }
}

export async function proposeTaskAssignments(): Promise<TaskAssignmentProposal[]> {
  const response = await api.post<{ status: string; proposals?: TaskAssignmentProposal[] }>(
    '/sessions/tasks/propose-assignments'
  );
  return response.data?.status === 'success' ? response.data.proposals ?? [] : [];
}

export async function confirmTaskAssignments(
  assignments: Array<{
    taskId: string;
    assigneeUid?: string;
    releaseToClaim?: boolean;
    pid?: string;
  }>
): Promise<number> {
  const response = await api.post<{ status: string; appliedCount?: number }>(
    '/sessions/tasks/confirm-assignments',
    { assignments }
  );
  if (response.data?.status !== 'success') {
    throw new Error('Unable to confirm assignments');
  }
  return response.data.appliedCount ?? 0;
}
